<template>
	<div class="formgroup-container">
		<div v-if="field.error" class="error field-error">
			{{ $t(field.error) }}
		</div>
		<div class="formgroup-heading">
			<strong>{{ $t(`form${capitalize(field.name)}`) }}</strong>
		</div>
		<div class="checkboxes">
			<label v-for="value in field.options" :key="value" :for="`${field.name}-${value}`">
				<input
					:id="`${field.name}-${value}`"
					:value="value"
					type="checkbox"
					:name="`${field.name}[]`"
					:checked="selectedValues.includes(value)"
					@change="updateSelection"
				/>
				{{ $t(`form${capitalize(value)}`) }}
			</label>
		</div>
		<div v-if="field.allowOther" class="checkboxes block">
			<label :for="`${field.name}-other`">
				<input
					:id="`${field.name}-other`"
					:checked="isOtherSelected"
					name="otherSpecify"
					type="checkbox"
					class="toggle-extra-option"
					@change="toggleOther"
				/>{{ $t('formOtherSpecify') }}
			</label>
			<transition name="extra-option">
				<input
					v-show="isOtherSelected"
					ref="otherInput"
					type="text"
					class="extra-option"
					@input="updateOtherText"
				/>
			</transition>
		</div>
	</div>
</template>

<script setup>
const selectedValues = ref([]);
const isOtherSelected = ref(false);
const otherInput = ref(null);
const otherTextValue = ref('');
const model = defineModel('checkboxgroup', { type: Array, default: () => [] });

defineProps({
	field: { type: Object, required: true },
});

/**
 * Update the selected values based on checkbox changes.
 *
 * @param {Event} event The change event
 *
 * @returns {void} Nothing.
 */
const updateSelection = (event) => {
	const value = event.target.value;
	const isChecked = event.target.checked;

	if (isChecked && !selectedValues.value.includes(value)) {
		selectedValues.value.push(value);
	} else {
		selectedValues.value = selectedValues.value.filter((item) => item !== value);
	}

	model.value = selectedValues.value;
};

/**
 * Toggle the "Other" option and toggle the value in the selected values.
 *
 * @returns {void} Nothing.
 */
const toggleOther = () => {
	isOtherSelected.value = !isOtherSelected.value;

	setOtherState();

	if (isOtherSelected.value) {
		void nextTick(() => {
			otherInput.value?.focus();
		});
	}

	model.value = selectedValues.value;
};

/**
 * Update the "Other" text input and update the selected values accordingly.
 *
 * @param {Event} event The input event
 *
 * @returns {void} Nothing.
 */
const updateOtherText = (event) => {
	otherTextValue.value = event.target.value.trim();

	setOtherState();

	model.value = selectedValues.value;
};

/**
 * Set the "Other" option in the selected values to match the checkbox and
 * its text input.
 *
 * @returns {void} Nothing.
 */
const setOtherState = () => {
	// We will replace any existing "other" value with the new one, or remove it if not selected.
	selectedValues.value = selectedValues.value.filter((item) => !item.startsWith('other:'));

	if (isOtherSelected.value && otherTextValue.value) {
		selectedValues.value.push(`other:${otherTextValue.value}`);
	}
};
</script>

<style lang="scss" scoped>
.formgroup-container {
	margin: 0 0 25px;

	.formgroup-heading {
		margin-bottom: 15px;
	}

	.checkboxes {
		display: flex;
		flex-flow: column;
		gap: 10px;
		margin: 5px 0 10px;

		&.block {
			display: block;
		}

		.extra-option {
			margin: 15px 0 0;
		}

		.extra-option-enter-active,
		.extra-option-leave-active {
			max-height: 60px;
			overflow: hidden;
			transition:
				max-height 0.3s ease-in-out,
				margin 0.3s ease-in-out,
				padding 0.3s ease-in-out,
				opacity 0.3s ease-in-out;
		}

		.extra-option-enter-from,
		.extra-option-leave-to {
			max-height: 0;
			padding-top: 0;
			padding-bottom: 0;
			margin-top: 0;
			opacity: 0;
		}

		label {
			width: auto;
			padding: 0;
			font-size: 0.9em;
			margin: 0 5px 0 0;
			display: flex;
			gap: 10px;
			align-items: center;

			input {
				width: auto;
				padding: 0;
				appearance: auto;
				margin: -2px 6px 0 0;
			}
		}
	}
}

.field-error {
	margin-bottom: 25px;
}
</style>
